import formatPrice from '@honda-canada/js-utilities/lib/formatPrice';
import React, { useMemo } from 'react';
import { Box, Optional } from '@honda-canada/design-system-react';
import { useTranslation } from 'react-i18next';
import PaymentCalculatorSectionTitle from '../shared/PaymentCalculatorSectionTitle';
import themeStyles from '../AdditionalSavings/AdditionalSavings.styles';
import useIncentiveOffers from './useIncentiveOffers';
import { LabeledCheckbox, LabeledRadio } from '../shared/LabeledItem';
import { OFFER_TYPE_DOLLAR, OFFER_TYPE_PERCENT } from './constants';
import { loyaltyOfferChanged, primaryCalculatorId, selectLoyaltyOffer, } from '../../../core/reducers/financialCalculators';
import { loyaltyPrimaryChanged } from '../../../core/reducers/offers';
import { useAppDispatch, useAppSelector } from '../../../core/utils';
import { useLanguage } from '../../../utils/sitecoreContext';
import isNonNullable from '../../../utils/isNonNullable';
const ItemsContainer = themeStyles.apply(Box, 'CheckboxesContainer');
const OfferLoyalty = ({ calculatorId, disableAllFields, handlePaymentOptionsChanged, loyaltyOffers, onLoyaltyClick, }) => {
    const { t } = useTranslation();
    const language = useLanguage();
    const dispatch = useAppDispatch();
    const isPrimary = calculatorId === primaryCalculatorId;
    const selectedLoyaltyOfferKey = useAppSelector(state => selectLoyaltyOffer(state, calculatorId));
    const { currentVehicleTransmissionKey: transmissionKey, paymentMethod } = useIncentiveOffers(calculatorId);
    const loyaltyList = useMemo(() => {
        if (!Array.isArray(loyaltyOffers)) {
            return [];
        }
        const offers = loyaltyOffers.map((offer) => {
            const isSelectedLoyaltyOfferKey = selectedLoyaltyOfferKey === offer.key;
            const sharedOfferInfo = {
                ...offer,
                disabled: disableAllFields,
                label: '',
                inputName: offer.key,
                isSelected: isPrimary ? offer.isSelected : isSelectedLoyaltyOfferKey,
            };
            if (offer.type === OFFER_TYPE_PERCENT) {
                return {
                    ...sharedOfferInfo,
                    label: t('Pages.Bap.loyaltyRateReductionLabel'),
                    rightSideText: `-${offer.value * 100}%`,
                    tooltipText: t('Pages.Bap.loyaltyRateReductionTooltipText'),
                };
            }
            if (offer.type === OFFER_TYPE_DOLLAR) {
                return {
                    ...sharedOfferInfo,
                    label: t('Pages.Bap.loyaltyRebateLabel'),
                    rightSideText: `-${formatPrice(offer.value, language, 2)}`,
                    tooltipText: t('Pages.Bap.loyaltyRebateTooltipText'),
                };
            }
            return null;
        });
        return offers.filter(isNonNullable);
    }, [loyaltyOffers, selectedLoyaltyOfferKey, isPrimary, disableAllFields, language, t]);
    const handlerOnCheckboxClick = (offer) => {
        const { key } = offer;
        const isSelected = selectedLoyaltyOfferKey === key;
        if (isSelected) {
            dispatch(loyaltyOfferChanged({ calculatorId, offerKey: '' }));
            dispatch(loyaltyPrimaryChanged({ calculatorId, offerKey: '', transmissionKey, paymentMethod }));
        }
        else {
            dispatch(loyaltyOfferChanged({ calculatorId, offerKey: key }));
            dispatch(loyaltyPrimaryChanged({ calculatorId, offerKey: key, transmissionKey, paymentMethod }));
        }
        handlePaymentOptionsChanged === null || handlePaymentOptionsChanged === void 0 ? void 0 : handlePaymentOptionsChanged();
        onLoyaltyClick === null || onLoyaltyClick === void 0 ? void 0 : onLoyaltyClick(offer, !isSelected);
    };
    const handleRadioButtonChange = (offer) => {
        const { key } = offer;
        const isSelected = selectedLoyaltyOfferKey === key;
        if (!isSelected) {
            dispatch(loyaltyOfferChanged({ calculatorId, offerKey: key }));
            dispatch(loyaltyPrimaryChanged({ calculatorId, offerKey: key, transmissionKey, paymentMethod }));
            onLoyaltyClick === null || onLoyaltyClick === void 0 ? void 0 : onLoyaltyClick(offer, true);
        }
        handlePaymentOptionsChanged === null || handlePaymentOptionsChanged === void 0 ? void 0 : handlePaymentOptionsChanged();
    };
    if (!loyaltyList.length)
        return null;
    return (React.createElement(React.Fragment, null,
        React.createElement(PaymentCalculatorSectionTitle, { mt: "l", "data-testid": "cy-bap-loyalty-incentives-title" }, t('Pages.Bap.loyaltyOfferLabel')),
        React.createElement(ItemsContainer, { "data-testid": "cy-view-offers-content-wrapper-loyaltyOffers" },
            React.createElement(Optional, { when: loyaltyList.length === 1 }, loyaltyList.map(offer => (React.createElement(LabeledCheckbox, { checked: offer.isSelected, dataTestId: `cy-offer-loyalty-checkbox-${offer.key}`, disabled: disableAllFields, key: offer.key, label: t('Pages.Bap.loyaltyRebateLabel'), name: offer.name, onClick: () => handlerOnCheckboxClick(offer), rightSideText: offer.rightSideText, tooltipText: t('Pages.Bap.loyaltyRebateTooltipText') })))),
            React.createElement(Optional, { when: loyaltyList.length > 1 }, loyaltyList.map(offer => (React.createElement(LabeledRadio, { ...offer, checked: offer.isSelected, dataTestId: `cy-offer-loyalty-radio-${offer.key}`, key: offer.inputName, name: offer.inputName, onClick: () => handleRadioButtonChange(offer) })))))));
};
export default OfferLoyalty;
